import React, {useCallback, useEffect, useState} from 'react';
import {fetchPoliticalComicManifest} from "./utilities/dataSources";
import {mobile} from "./services/mobileBridge";

type ComicStrip = {
    id: string;
    region: string;
    regionLabel: string;
    article: {
        title: string;
        publishedAt: string;
        sourceNames: string[];
        urls: string[];
    };
    story: {
        title: string;
        summary: string;
        importance: string;
        satiricalAngle: string;
    };
    comic: {
        title: string;
        punchline: string;
        layout: { rows: number; columns: number };
        style: { id: string; name: string; description?: string };
        panels: unknown[];
    };
    image: {
        key: string;
        fileName: string;
        mediaType: string;
        url: string;
    };
};

type ComicManifest = {
    schemaVersion: number;
    date: string;
    generatedAt: string;
    strips: ComicStrip[];
};

function formatLongDate(dateStr?: string) {
    if (!dateStr) return '';
    const date = new Date(`${dateStr}T00:00:00`);
    return date.toLocaleDateString(undefined, {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});
}

function formatShortDate(dateStr?: string) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'});
}

// Feature the Africa strip (most relevant to this audience), keep the rest in manifest order.
function orderStrips(strips: ComicStrip[]) {
    const ordered = [...strips];
    const africaIndex = ordered.findIndex(strip => strip.region === 'africa');
    if (africaIndex > 0) {
        const [africa] = ordered.splice(africaIndex, 1);
        ordered.unshift(africa);
    }
    return ordered;
}

function RegionTag({region, label}: { region: string; label: string }) {
    return <span className={`pc-region-tag pc-region-${region}`}>{label}</span>;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;

function clampScale(value: number) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

function ImageViewer({src, alt, onClose}: { src: string; alt: string; onClose: () => void }) {
    const [scale, setScale] = React.useState(1);
    const [offset, setOffset] = React.useState({x: 0, y: 0});
    const [dragging, setDragging] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const pointersRef = React.useRef(new Map<number, {x: number; y: number}>());
    const dragRef = React.useRef<{id: number; startX: number; startY: number; origX: number; origY: number} | null>(null);
    const pinchRef = React.useRef<{startDist: number; startScale: number; startMidX: number; startMidY: number; origX: number; origY: number} | null>(null);

    const clampOffset = React.useCallback((candidate: {x: number; y: number}, currentScale: number) => {
        const container = containerRef.current;
        if (!container) return candidate;
        const rect = container.getBoundingClientRect();
        const maxX = (rect.width * (currentScale - 1)) / 2;
        const maxY = (rect.height * (currentScale - 1)) / 2;
        return {
            x: Math.min(maxX, Math.max(-maxX, candidate.x)),
            y: Math.min(maxY, Math.max(-maxY, candidate.y)),
        };
    }, []);

    React.useEffect(() => {
        setOffset(current => clampOffset(current, scale));
    }, [scale, clampOffset]);

    React.useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const handleWheel = (event: WheelEvent) => {
            event.preventDefault();
            const factor = event.deltaY < 0 ? 1.2 : 1 / 1.2;
            setScale(previous => clampScale(previous * factor));
        };
        container.addEventListener('wheel', handleWheel, {passive: false});
        return () => container.removeEventListener('wheel', handleWheel);
    }, []);

    const zoomBy = (factor: number) => setScale(previous => clampScale(previous * factor));

    const resetZoom = () => {
        setScale(1);
        setOffset({x: 0, y: 0});
    };

    const onPointerDown = (event: React.PointerEvent) => {
        if (event.pointerType === 'mouse') {
            dragRef.current = {
                id: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                origX: offset.x,
                origY: offset.y,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
            return;
        }
        pointersRef.current.set(event.pointerId, {x: event.clientX, y: event.clientY});
        if (pointersRef.current.size === 1) {
            dragRef.current = {
                id: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                origX: offset.x,
                origY: offset.y,
            };
        } else if (pointersRef.current.size === 2) {
            const [first, second] = Array.from(pointersRef.current.values());
            pinchRef.current = {
                startDist: Math.hypot(second.x - first.x, second.y - first.y),
                startScale: scale,
                startMidX: (first.x + second.x) / 2,
                startMidY: (first.y + second.y) / 2,
                origX: offset.x,
                origY: offset.y,
            };
            dragRef.current = null;
        }
    };

    const onPointerMove = (event: React.PointerEvent) => {
        if (event.pointerType === 'mouse') {
            const drag = dragRef.current;
            if (drag && drag.id === event.pointerId) {
                setOffset(clampOffset(
                    {
                        x: drag.origX + (event.clientX - drag.startX),
                        y: drag.origY + (event.clientY - drag.startY),
                    },
                    scale,
                ));
            }
            return;
        }
        if (!pointersRef.current.has(event.pointerId)) return;
        pointersRef.current.set(event.pointerId, {x: event.clientX, y: event.clientY});
        if (pointersRef.current.size === 2) {
            const [first, second] = Array.from(pointersRef.current.values());
            const distance = Math.hypot(second.x - first.x, second.y - first.y);
            const midX = (first.x + second.x) / 2;
            const midY = (first.y + second.y) / 2;
            if (!pinchRef.current) {
                pinchRef.current = {
                    startDist: distance,
                    startScale: scale,
                    startMidX: midX,
                    startMidY: midY,
                    origX: offset.x,
                    origY: offset.y,
                };
                return;
            }
            const pinch = pinchRef.current;
            const nextScale = clampScale(pinch.startScale * (distance / (pinch.startDist || 1)));
            setOffset(clampOffset(
                {
                    x: pinch.origX + (midX - pinch.startMidX),
                    y: pinch.origY + (midY - pinch.startMidY),
                },
                nextScale,
            ));
            setScale(nextScale);
        } else if (pointersRef.current.size === 1) {
            pinchRef.current = null;
            const drag = dragRef.current;
            if (drag && drag.id === event.pointerId) {
                setOffset(clampOffset(
                    {
                        x: drag.origX + (event.clientX - drag.startX),
                        y: drag.origY + (event.clientY - drag.startY),
                    },
                    scale,
                ));
            }
        }
    };

    const endPointer = (event: React.PointerEvent) => {
        if (event.pointerType === 'mouse') {
            if (dragRef.current?.id === event.pointerId) dragRef.current = null;
            setDragging(false);
            return;
        }
        pointersRef.current.delete(event.pointerId);
        if (pointersRef.current.size < 2) pinchRef.current = null;
        if (pointersRef.current.size === 0) dragRef.current = null;
    };

    const onDoubleClick = () => {
        if (scale > 1) {
            resetZoom();
        } else {
            setScale(2);
        }
    };

    const cursor = dragging ? 'grabbing' : scale > 1 ? 'grab' : 'zoom-in';

    return (
        <div
            className={`pc-viewer${dragging ? ' dragging' : ''}`}
            ref={containerRef}
            style={{cursor}}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onDoubleClick={onDoubleClick}
        >
            <img
                src={src}
                alt={alt}
                draggable={false}
                className="pc-viewer-img"
                style={{transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`}}
            />
            <button className="pc-viewer-close" onClick={onClose} aria-label="Close viewer">✕</button>
            <div className="pc-viewer-hint">Drag to pan · scroll or pinch to zoom · double-click to toggle</div>
            <div className="pc-viewer-toolbar">
                <button onClick={() => zoomBy(1 / 1.25)} aria-label="Zoom out">−</button>
                <span className="pc-viewer-scale">{Math.round(scale * 100)}%</span>
                <button onClick={() => zoomBy(1.25)} aria-label="Zoom in">+</button>
                <button onClick={resetZoom} aria-label="Reset zoom">⤢</button>
            </div>
        </div>
    );
}

function StripModal({strip, onClose}: { strip: ComicStrip; onClose: () => void }) {
    const [viewerOpen, setViewerOpen] = React.useState(false);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            if (viewerOpen) {
                setViewerOpen(false);
            } else {
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [viewerOpen, onClose]);

    useEffect(() => {
        return mobile.onNativeBack(() => {
            if (viewerOpen) {
                setViewerOpen(false);
            } else {
                onClose();
            }
        });
    }, [viewerOpen, onClose]);

    const handleShare = async () => {
        await mobile.share({
            title: strip.comic.title,
            text: `“${strip.comic.punchline}”`,
            url: strip.image.url,
        });
    };

    return (
        <>
            <div className="modal-backdrop" onClick={onClose}>
                <div className="modal pc-modal" onClick={event => event.stopPropagation()}>
                    <button className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
                    <div className="pc-modal-meta">
                        <RegionTag region={strip.region} label={strip.regionLabel}/>
                        {strip.comic.style?.name && <span className="pc-style-chip">{strip.comic.style.name}</span>}
                    </div>
                    <h2>{strip.comic.title}</h2>
                    <img
                        src={strip.image.url}
                        alt={strip.comic.title}
                        className="pc-modal-img pc-modal-img-zoomable"
                        onClick={() => setViewerOpen(true)}
                    />
                    <p className="pc-modal-punchline">“{strip.comic.punchline}”</p>
                    <div className="pc-modal-section">
                        <h3>Backstory</h3>
                        <p>{strip.story.summary}</p>
                        <p><strong>Why it matters:</strong> {strip.story.importance}</p>
                        <p><strong>Satirising:</strong> {strip.story.satiricalAngle}</p>
                    </div>
                    <div className="pc-modal-article">
                        <span className="pc-article-title">Based on: {strip.article.title}</span>
                        <span className="pc-article-source">{strip.article.sourceNames.join(', ')} &middot; {formatShortDate(strip.article.publishedAt)}</span>
                        <a className="pc-article-link" href={strip.article.urls[0]} target="_blank" rel="noreferrer">Read the story ↗</a>
                    </div>
                    <div className="pc-modal-actions">
                        <button className="modal-btn modal-btn-primary" onClick={() => setViewerOpen(true)}>🔍 Zoom</button>
                        <button className="modal-btn" onClick={handleShare}>📤 Share</button>
                        <a className="modal-btn" href={strip.image.url} target="_blank" rel="noreferrer">Open full size</a>
                        <button className="modal-btn" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
            {viewerOpen && (
                <ImageViewer src={strip.image.url} alt={strip.comic.title} onClose={() => setViewerOpen(false)}/>
            )}
        </>
    );
}

export default function PoliticalComic({country}: { country: 'south-africa' | 'nigeria' }) {
    const [manifest, setManifest] = useState<ComicManifest | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selected, setSelected] = useState<ComicStrip | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        fetchPoliticalComicManifest()
            .then(response => setManifest(response.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const strips = manifest ? orderStrips(manifest.strips) : [];
    const featured = strips[0];
    const rest = strips.slice(1);

    return (
        <div className="App pc-page">
            <a href={`/media/${country}`} className="game-back-link">&larr; Back to Media</a>
            <header className="pc-header">
                <div className="pc-badge">🗞️ Daily</div>
                <h1>Political Comic Strip</h1>
                <p className="pc-sub">
                    Six four-panel editorial comics drawn from today's news — the US, South America, Europe,
                    Asia and Africa — starring Casey Common and his terrier sidekick Ledger.
                </p>
                <div className="pc-meta">
                    {manifest && <span className="pc-meta-chip">📅 {formatLongDate(manifest.date)}</span>}
                    <span className="pc-meta-chip">✍️ Fresh every day</span>
                    <span className="pc-meta-chip">🌍 5 regions</span>
                </div>
            </header>

            {loading && <div className="pc-spinner" role="status"/>}
            {error && !loading && (
                <div className="pc-error">
                    <div className="pc-error-emoji">📰</div>
                    <h2>Today's strips aren't in yet</h2>
                    <p>There was a problem reaching the comic archive. Check back soon or try again.</p>
                    <button className="center-btn" onClick={load}>Try again</button>
                </div>
            )}

            {manifest && !loading && (
                <main className="pc-main">
                    {featured && (
                        <article className="pc-featured" onClick={() => setSelected(featured)} role="button">
                            <img src={featured.image.url} alt={featured.comic.title} className="pc-featured-img"/>
                            <div className="pc-featured-body">
                                <div className="pc-featured-top">
                                    <RegionTag region={featured.region} label={featured.regionLabel}/>
                                    <span className="pc-featured-label">Featured</span>
                                </div>
                                <h2 className="pc-featured-title">{featured.comic.title}</h2>
                                <p className="pc-featured-punchline">“{featured.comic.punchline}”</p>
                            </div>
                        </article>
                    )}
                    {rest.length > 0 && (
                        <section className="pc-more">
                            <h2 className="pc-section-label">More from today</h2>
                            <div className="pc-grid">
                                {rest.map(strip => (
                                    <article key={strip.id} className="pc-card" onClick={() => setSelected(strip)} role="button">
                                        <img src={strip.image.url} alt={strip.comic.title} className="pc-card-img" loading="lazy"/>
                                        <div className="pc-card-body">
                                            <RegionTag region={strip.region} label={strip.regionLabel}/>
                                            <h3 className="pc-card-title">{strip.comic.title}</h3>
                                            <p className="pc-card-punchline">“{strip.comic.punchline}”</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>
                    )}
                </main>
            )}

            {selected && <StripModal strip={selected} onClose={() => setSelected(null)}/>}
        </div>
    );
}
